import React, { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import ChartCard from "./ChartCard";
import { getChartCardStatus } from "./charts.utils";
import ExportCsvButton from "./ExportCsvButton";
import RankedBarChart from "./RankedBarChart";
import useRankedExpansion from "./useRankedExpansion";

import type { JSX, ReactNode } from "react";

import type { Query } from "@/src/api/BaseAPI";

import type { ChartSeries, CsvExport, RankedDatum } from "./charts.types";

interface RankedQueryCardProps<TResponse, TKey extends string> {
  title: string;
  query: Query<TResponse>;
  seriesList: readonly ChartSeries<TKey>[];
  /** Must keep a stable identity (module-level function) to preserve the memo. */
  buildRankedList: (response: TResponse | undefined) => RankedDatum<TKey>[];
  extraActions?: ReactNode;
  csvExport?: CsvExport;
}

function RankedQueryCard<TResponse, TKey extends string>({
  title,
  query,
  seriesList,
  buildRankedList,
  extraActions,
  csvExport,
}: RankedQueryCardProps<TResponse, TKey>): JSX.Element {
  const { t } = useTranslation("editor-analytics");
  const { data, isLoading, isError, isFetching } = useQuery(query);

  const rankedList = useMemo(
    () => buildRankedList(data),
    [buildRankedList, data],
  );
  const { limit, actions } = useRankedExpansion(rankedList.length);

  return (
    <ChartCard
      isFetching={isFetching}
      title={title}
      actions={
        actions || extraActions || csvExport ? (
          <React.Fragment>
            {actions}
            {extraActions}
            {csvExport ? (
              <ExportCsvButton
                fetchCsv={csvExport.fetchCsv}
                fileName={csvExport.fileName}
              />
            ) : null}
          </React.Fragment>
        ) : undefined
      }
      status={getChartCardStatus({
        isLoading,
        isError,
        isEmpty: !rankedList.length,
      })}
    >
      <RankedBarChart
        dataList={rankedList}
        limit={limit}
        othersLabel={t("charts.others")}
        seriesList={seriesList}
      />
    </ChartCard>
  );
}

export default RankedQueryCard;
